import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

function monthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const startDow = (first.getDay() + 6) % 7; // Monday=0
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(year, month, d));
  return cells;
}

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function AdminCalendar() {
  const [cursor, setCursor] = useState(new Date());
  const [data, setData] = useState({ reservations: [], blocked: [] });
  const y = cursor.getFullYear(), m = cursor.getMonth();

  useEffect(() => {
    const start = fmt(new Date(y, m, 1));
    const end = fmt(new Date(y, m + 1, 0));
    api.get(`/admin/calendar?start=${start}&end=${end}`).then((r) => setData(r.data)).catch(() => {});
  }, [y, m]);

  const cells = monthMatrix(y, m);
  const byDate = {};
  data.reservations.forEach((r) => { (byDate[r.date] = byDate[r.date] || []).push(r); });
  const blockedDates = new Set(data.blocked.map((b) => b.date));

  return (
    <div data-testid="admin-calendar">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display font-black text-3xl">Calendrier</h1>
        <div className="flex items-center gap-3">
          <button onClick={() => setCursor(new Date(y, m - 1, 1))} className="btn-ghost !p-2.5"><ChevronLeft size={18} /></button>
          <span className="font-display font-bold text-lg w-40 text-center capitalize">{cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</span>
          <button onClick={() => setCursor(new Date(y, m + 1, 1))} className="btn-ghost !p-2.5"><ChevronRight size={18} /></button>
        </div>
      </div>

      <div className="card-dark p-4">
        <div className="grid grid-cols-7 gap-2 mb-2 text-center text-xs text-slate-500 font-semibold">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const ds = fmt(d);
            const res = byDate[ds] || [];
            const isBlocked = blockedDates.has(ds);
            return (
              <div key={i} className={`min-h-[92px] rounded-lg border p-2 ${isBlocked ? "border-red-500/30 bg-red-500/5" : "border-white/8 bg-black/20"}`}>
                <div className="text-xs text-slate-500">{d.getDate()}</div>
                {isBlocked && <div className="text-[10px] text-red-400 mt-1">Fermé</div>}
                <div className="space-y-1 mt-1">
                  {res.slice(0, 3).map((r) => (
                    <div key={r.id} className={`text-[10px] px-1.5 py-0.5 rounded truncate ${r.status === "CONFIRMED" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`} title={`${r.start_time} ${r.first_name} ${r.last_name}`}>
                      {r.start_time} {r.last_name}
                    </div>
                  ))}
                  {res.length > 3 && <div className="text-[10px] text-slate-500">+{res.length - 3}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex gap-4 mt-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500/40" /> Confirmée</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-500/40" /> En attente</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-500/40" /> Fermé</span>
      </div>
    </div>
  );
}
